"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import PricingModal from "@/components/peeqd/PricingModal";
import { trackFunnelEvent } from "@/libs/ga";

export interface OpenPricingModalOptions {
  source?: string;
  username?: string;
  tool_id?: string;
}

interface PricingModalContextValue {
  isOpen: boolean;
  openPricingModal: (options?: OpenPricingModalOptions) => void;
  closePricingModal: () => void;
}

const PricingModalContext = createContext<PricingModalContextValue | undefined>(undefined);

export function PricingModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const openPricingModal = useCallback((options?: OpenPricingModalOptions) => {
    trackFunnelEvent("monetization", "pricing_viewed", {
      source: options?.source ?? "pricing_modal",
      purchase_trigger: "voluntary",
      username: options?.username,
      tool_id: options?.tool_id,
    });
    setIsOpen(true);
  }, []);
  const closePricingModal = useCallback(() => setIsOpen(false), []);

  const value = useMemo<PricingModalContextValue>(
    () => ({ isOpen, openPricingModal, closePricingModal }),
    [isOpen, openPricingModal, closePricingModal]
  );

  return (
    <PricingModalContext.Provider value={value}>
      {children}
      <PricingModal isOpen={isOpen} onClose={closePricingModal} />
    </PricingModalContext.Provider>
  );
}

export function usePricingModal(): PricingModalContextValue {
  const ctx = useContext(PricingModalContext);
  if (!ctx) {
    throw new Error("usePricingModal must be used within a PricingModalProvider");
  }
  return ctx;
}
